import { useState } from "react";
import { Link, useNavigate } from "react-router";
import type { StudentSkillTopic } from "@edu/shared";
import {
  SHEET_BUSY,
  SHEET_NOTE,
  SHEET_PLACEHOLDER,
  SHEET_QUESTION,
  picksParam,
} from "./student-skills-text.ts";
import { getDiagnosticSheet } from "./student-skills-api.ts";

/**
 * Выбор темы и кнопка «Собрать диагностический лист» (4.2 и 7.3 проекта).
 *
 * Вопрос преподавателю задаётся тот, ответ на который у него всегда есть: не
 * «где у него дыры», за этим он сюда и пришёл, а «какую тему вы собираетесь с
 * ним вести». Дальше сервер соберёт лист по её пререквизитам, и после одного
 * решённого листа панель наполнится.
 *
 * ⚠️ **Ручка состава листа может быть ещё не выкачена**, она пишется
 * параллельно. Поэтому отказ здесь не показывается ошибкой, а уводит в обычную
 * форму выдачи: преподаватель выберет темы руками и потеряет подсказку, а не
 * действие. Кнопка, которая ничего не делает, хуже кнопки, которая делает
 * меньше обещанного.
 *
 * ⚠️ Список тем это `unmeasured` из ответа ручки 6.1, то есть темы, по которым
 * платформа умеет давать задания, а ученику их не давали. Своего справочника
 * панель не запрашивает: лишний круг по сети на канале 128 кбит/с виден как
 * подвисание, а этот список уже приехал.
 */
export function DiagnosticSheetAction(props: {
  studentUserId: string;
  topics: readonly StudentSkillTopic[];
  /** Слово на кнопке. У холодного старта и у «всё держится» оно разное. */
  label: string;
}) {
  const navigate = useNavigate();
  const [skill, setSkill] = useState("");
  const [busy, setBusy] = useState(false);

  const form = `/homework/new?studentId=${props.studentUserId}`;

  // Тем для выбора нет вовсе: платформа умеет всё, что ученику уже давали.
  // Кнопка остаётся, но ведёт прямо в форму: там темы выбираются руками
  if (props.topics.length === 0) {
    return (
      <p className="gaps__act">
        <Link className="btn" to={form}>
          {props.label}
        </Link>
      </p>
    );
  }

  const go = (): void => {
    if (skill === "" || busy) return;
    setBusy(true);
    void (async () => {
      let to = form;
      try {
        to = `${form}&picks=${picksParam((await getDiagnosticSheet(skill)).picks)}`;
      } catch {
        /* состава нет: открываем форму как есть, тему преподаватель выберет сам */
      }
      navigate(to);
    })();
  };

  return (
    <div className="gaps__act">
      <label className="lbl" htmlFor="gaps-topic">
        {SHEET_QUESTION}
      </label>
      <div className="gaps__actrow">
        <select
          className="fld"
          id="gaps-topic"
          value={skill}
          disabled={busy}
          onChange={(e) => setSkill(e.target.value)}
        >
          <option value="">{SHEET_PLACEHOLDER}</option>
          {props.topics.map((t) => (
            <option key={t.skill} value={t.skill}>
              {t.name}
            </option>
          ))}
        </select>
        <button className="btn" type="button" disabled={skill === "" || busy} onClick={go}>
          {busy ? SHEET_BUSY : props.label}
        </button>
      </div>
      {/* Что именно соберётся, сказано заранее: лист идёт не по самой теме, а по
          тому, на чём она стоит, и это ровно то, чего от него не ждут */}
      <p className="muted">{SHEET_NOTE}</p>
    </div>
  );
}
